import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Card, Statistic, List, Badge, Progress, Spin, Empty } from 'antd';
import { 
  SmileOutlined, 
  FrownOutlined, 
  MehOutlined, 
  CalendarOutlined,
  FireOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { emotionRecordApi, emotionApi } from '../services/api';
import { formatDate } from '../utils/dateUtils';

const { Title, Text } = Typography;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [emotions, setEmotions] = useState([]);
  const [emotionRecords, setEmotionRecords] = useState([]);
  const [stats, setStats] = useState({
    totalRecords: 0,
    positiveCount: 0,
    negativeCount: 0,
    neutralCount: 0,
    mostFrequentEmotion: null
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 获取所有情绪
        const emotionsResponse = await emotionApi.getAllEmotions();
        setEmotions(emotionsResponse.data);
        
        // 获取用户的情绪记录
        const recordsResponse = await emotionRecordApi.getUserEmotionRecords(currentUser.id);
        const records = recordsResponse.data;
        setEmotionRecords(records);
        
        // 计算统计数据
        const positiveRecords = records.filter(record => record.emotion?.intensity > 5);
        const negativeRecords = records.filter(record => record.emotion?.intensity < 5);
        const neutralRecords = records.filter(record => record.emotion?.intensity === 5);
        
        // 获取最常见的情绪
        const emotionFrequency = {};
        records.forEach(record => {
          if (record.emotion?.id) {
            emotionFrequency[record.emotion.id] = (emotionFrequency[record.emotion.id] || 0) + 1;
          }
        });
        
        let mostFrequentEmotionId = null;
        let maxFrequency = 0;
        
        Object.entries(emotionFrequency).forEach(([emotionId, frequency]) => {
          if (frequency > maxFrequency) {
            maxFrequency = frequency;
            mostFrequentEmotionId = emotionId;
          }
        });
        
        const mostFrequentEmotion = emotions.find(emotion => emotion.id === parseInt(mostFrequentEmotionId));
        
        setStats({
          totalRecords: records.length,
          positiveCount: positiveRecords.length,
          negativeCount: negativeRecords.length,
          neutralCount: neutralRecords.length,
          mostFrequentEmotion
        });
      } catch (error) {
        console.error('获取数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser]);

  // 获取情绪图标
  const getEmotionIcon = (intensity) => {
    if (!intensity && intensity !== 0) return <MehOutlined />;
    if (intensity > 5) return <SmileOutlined style={{ color: '#52c41a' }} />;
    if (intensity < 5) return <FrownOutlined style={{ color: '#f5222d' }} />;
    return <MehOutlined style={{ color: '#faad14' }} />;
  };

  // 获取情绪徽章颜色
  const getEmotionBadgeColor = (intensity) => {
    if (!intensity && intensity !== 0) return 'default';
    if (intensity > 7) return 'success';
    if (intensity > 5) return 'processing';
    if (intensity < 3) return 'error';
    if (intensity < 5) return 'warning';
    return 'default';
  };

  // 获取最近的5条情绪记录
  const recentRecords = emotionRecords
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>加载数据中...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <Title level={2}>
        <span>{currentUser?.fullName || currentUser?.username || '用户'}</span>
        {' '}的情绪仪表盘
      </Title>
      <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
        查看您的情绪记录总览和最新动态
      </Text>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="情绪记录总数"
              value={stats.totalRecords}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="积极情绪"
              value={stats.positiveCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<SmileOutlined />}
              suffix={stats.totalRecords > 0 ? `${Math.round(stats.positiveCount / stats.totalRecords * 100)}%` : '0%'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="消极情绪"
              value={stats.negativeCount}
              valueStyle={{ color: '#f5222d' }}
              prefix={<FrownOutlined />}
              suffix={stats.totalRecords > 0 ? `${Math.round(stats.negativeCount / stats.totalRecords * 100)}%` : '0%'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="中性情绪"
              value={stats.neutralCount}
              valueStyle={{ color: '#faad14' }}
              prefix={<MehOutlined />}
              suffix={stats.totalRecords > 0 ? `${Math.round(stats.neutralCount / stats.totalRecords * 100)}%` : '0%'}
            />
          </Card>
        </Col>
      </Row>

      {/* 情绪分析和最近记录 */}
      <Row gutter={[16, 16]}>
        {/* 情绪分布 */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <LineChartOutlined /> 情绪强度分布
              </span>
            } 
            style={{ marginBottom: '16px', height: '100%' }}
          >
            {emotions.length > 0 && emotionRecords.length > 0 ? (
              <div>
                {emotions.map(emotion => {
                  const count = emotionRecords.filter(record => record.emotion?.id === emotion.id).length;
                  const percentage = stats.totalRecords > 0 ? Math.round(count / stats.totalRecords * 100) : 0;
                  
                  return (
                    <div key={emotion.id} style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Text>{emotion.name}</Text>
                        <Text>{count} 次 ({percentage}%)</Text>
                      </div>
                      <Progress 
                        percent={percentage} 
                        showInfo={false}
                        strokeColor={
                          emotion.intensity > 7 ? '#52c41a' : 
                          emotion.intensity > 5 ? '#1890ff' : 
                          emotion.intensity < 3 ? '#f5222d' : 
                          emotion.intensity < 5 ? '#faad14' : '#8c8c8c'
                        }
                      />
                    </div>
                  );
                })}
                
                {stats.mostFrequentEmotion && (
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <Badge 
                      color={getEmotionBadgeColor(stats.mostFrequentEmotion.intensity)} 
                      text={
                        <Text strong>
                          您最常记录的情绪是: {stats.mostFrequentEmotion.name}
                        </Text>
                      } 
                    />
                  </div>
                )}
              </div>
            ) : (
              <Empty description="暂无情绪记录数据" />
            )}
          </Card>
        </Col>

        {/* 最近记录 */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <FireOutlined /> 最近情绪记录
              </span>
            } 
            style={{ marginBottom: '16px', height: '100%' }}
          >
            {recentRecords.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={recentRecords}
                renderItem={record => (
                  <List.Item 
                    key={record.id}
                    actions={[
                      <a 
                        key="view-button" 
                        onClick={() => navigate(`/emotion-records/${record.id}`)}
                      >
                        查看详情
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={getEmotionIcon(record.emotion?.intensity)}
                      title={
                        <div>
                          <Badge 
                            color={getEmotionBadgeColor(record.emotion?.intensity)} 
                            text={record.emotion?.name || '未知情绪'} 
                          />
                        </div>
                      }
                      description={
                        <div>
                          <div>{record.event || '无事件描述'}</div>
                          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                            {formatDate(record.createdAt)}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="暂无情绪记录" />
            )}
            
            {recentRecords.length > 0 && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <a onClick={() => navigate('/emotion-records')}>查看全部记录</a>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage; 